import { ref, computed, readonly } from 'vue';
import { syncSystemBars } from '@/system-bars';
import { disableLiquidGlass, enableLiquidGlass } from '@/composables/liquidGlass';
import type { ThemeKey } from '@/types';

const STORAGE_KEY = 'pro-kanban.theme';

export const THEMES: ThemeKey[] = [
  'classic',
  'classic_dark',
  'glassmorph',
  'retro',
  'neumorph',
  'mesh_gradient',
  'pixel',
  'workcraft',
  'liquid_glass',
];

/** Themes painted on a light canvas — everything else is dark. */
const LIGHT_THEMES: ThemeKey[] = ['classic', 'neumorph', 'pixel'];

/** Tokens the company palette may replace; cleared together. */
const OVERRIDABLE = ['--c-accent', '--c-accent-soft', '--c-accent-content', '--c-secondary'] as const;

/**
 * What the current company allows its members to do with themes.
 *
 * Pushed in by the workspace store rather than read from it, because the store
 * already imports this module — a store import here would be a cycle.
 */
export interface CompanyThemePolicy {
  controlsTheme: boolean;
  allowedThemes: ThemeKey[];
  overrideColors: boolean;
  accentColor?: string | null;
  secondaryColor?: string | null;
}

const policy = ref<CompanyThemePolicy | null>(null);
const current = ref<ThemeKey>((localStorage.getItem(STORAGE_KEY) as ThemeKey) ?? 'classic');

/**
 * The themes the member may actually choose right now.
 *
 * An empty allow-list means the company turned the switch on but never narrowed
 * the list, which should not lock anybody out of every theme.
 */
export const availableThemes = computed<ThemeKey[]>(() => {
  const active = policy.value;
  if (!active?.controlsTheme) return THEMES;
  const allowed = THEMES.filter((theme) => active.allowedThemes.includes(theme));
  return allowed.length ? allowed : THEMES;
});

function hexToRgb(hex?: string | null): [number, number, number] | null {
  const match = /^#?([\da-f]{3}|[\da-f]{6})$/i.exec((hex ?? '').trim());
  if (!match) return null;
  const value = match[1].length === 3 ? [...match[1]].map((char) => char + char).join('') : match[1];
  const int = Number.parseInt(value, 16);
  return [(int >> 16) & 255, (int >> 8) & 255, int & 255];
}

function triplet(rgb: [number, number, number]): string {
  return rgb.map(Math.round).join(' ');
}

/** WCAG relative luminance, used to keep text on the accent readable. */
function luminance([r, g, b]: [number, number, number]): number {
  const channel = (value: number): number => {
    const ratio = value / 255;
    return ratio <= 0.03928 ? ratio / 12.92 : ((ratio + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

function mix(a: [number, number, number], b: [number, number, number], weightOfA: number): [number, number, number] {
  return [
    a[0] * weightOfA + b[0] * (1 - weightOfA),
    a[1] * weightOfA + b[1] * (1 - weightOfA),
    a[2] * weightOfA + b[2] * (1 - weightOfA),
  ];
}

/** Reads a token the active theme resolved, e.g. `--c-canvas` → `[244, 245, 248]`. */
function readToken(name: string): [number, number, number] | null {
  const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  const parts = raw.split(/[\s,]+/).map(Number);
  return parts.length === 3 && parts.every((part) => Number.isFinite(part))
    ? [parts[0], parts[1], parts[2]]
    : null;
}

/**
 * Paints the company palette over the active theme.
 *
 * Runs after every theme change: the soft and content variants are derived from
 * the theme's own canvas, so the same brand colour lands correctly on a light
 * and a dark theme.
 */
function applyColorOverrides(): void {
  const root = document.documentElement;
  for (const token of OVERRIDABLE) root.style.removeProperty(token);

  const active = policy.value;
  if (!active?.controlsTheme || !active.overrideColors) return;

  const accent = hexToRgb(active.accentColor);
  if (accent) {
    root.style.setProperty('--c-accent', triplet(accent));
    root.style.setProperty('--c-accent-content', luminance(accent) > 0.45 ? '16 18 26' : '255 255 255');
    const canvas = readToken('--c-canvas');
    if (canvas) root.style.setProperty('--c-accent-soft', triplet(mix(accent, canvas, 0.18)));
  }

  const secondary = hexToRgb(active.secondaryColor);
  if (secondary) root.style.setProperty('--c-secondary', triplet(secondary));
}

/**
 * Applies a theme by swapping one attribute on `<html>`.
 *
 * All colours are CSS variables, so the change is instant, works with any
 * locale, and never requires a reload. A theme the company disallows falls back
 * to the first one it does allow.
 */
export function applyTheme(theme: ThemeKey, persist = true): void {
  const allowed = availableThemes.value;
  if (!allowed.includes(theme)) theme = allowed[0] ?? 'classic';
  const light = LIGHT_THEMES.includes(theme);
  current.value = theme;
  document.documentElement.setAttribute('data-theme', theme);
  document.documentElement.style.colorScheme = light ? 'light' : 'dark';
  // On Android the system bars are painted outside the webview and would keep
  // the *device's* light/dark setting otherwise.
  syncSystemBars(light);
  // The refraction filters exist only while Liquid Glass is the active theme, so
  // no other theme carries an unused SVG filter definition around.
  if (theme === 'liquid_glass') enableLiquidGlass();
  else disableLiquidGlass();
  applyColorOverrides();
  if (persist) localStorage.setItem(STORAGE_KEY, theme);
}

/**
 * Installs the company's theme rules, re-clamping the active theme.
 *
 * Passing `null` (signing out, or a company that governs nothing) restores the
 * full theme list and drops any colour overrides.
 */
export function setCompanyThemePolicy(next: CompanyThemePolicy | null): void {
  policy.value = next;
  applyTheme(current.value);
}

export function companyControlsTheme(): boolean {
  return policy.value?.controlsTheme === true;
}

/** Cycles through the themes — bound to the `theme.toggle` shortcut. */
export function cycleTheme(): ThemeKey {
  const themes = availableThemes.value;
  const next = themes[(themes.indexOf(current.value) + 1) % themes.length];
  applyTheme(next);
  return next;
}

export function useTheme() {
  return {
    theme: readonly(current),
    themes: availableThemes,
    applyTheme,
    cycleTheme,
    isDark: () => !LIGHT_THEMES.includes(current.value),
  };
}

/** Applies the persisted theme before the first paint. */
export function initTheme(): void {
  applyTheme(current.value, false);
}
